// src/components/plm/GanttChart.jsx
import styles from "../../styles/PLMApp.module.css";
import { useState, useEffect, useRef, useMemo } from "react";
import { PLM_CATEGORIES } from "../../data/constants";
import { stepDocId } from "../../utils/utils";

const BAR_PALETTES = [
  ["rgba(200,169,110,0.82)", "rgba(200,169,110,1)", "#1a2420"],
  ["rgba(223,192,126,0.75)", "rgba(223,192,126,0.95)", "#1a2420"],
  ["rgba(168,200,158,0.82)", "rgba(168,200,158,1)", "#1a2420"],
  ["rgba(106,191,154,0.75)", "rgba(106,191,154,0.95)", "#1a2420"],
  ["rgba(142,184,208,0.82)", "rgba(142,184,208,1)", "#1a2420"],
  ["rgba(154,176,200,0.75)", "rgba(154,176,200,1)", "#1a2420"],
  ["rgba(208,168,136,0.82)", "rgba(208,168,136,1)", "#1a2420"],
  ["rgba(200,160,160,0.78)", "rgba(200,160,160,1)", "#1a2420"],
  ["rgba(184,160,208,0.78)", "rgba(184,160,208,1)", "#1a2420"],
  ["rgba(144,200,184,0.78)", "rgba(144,200,184,1)", "#1a2420"],
];

const PROGRESS_COLORS = {
  done: "rgba(106,191,154,0.55)",
  in_progress: "rgba(200,169,110,0.55)",
  waiting: "rgba(219,184,102,0.50)",
  not_started: "rgba(255,255,255,0.08)",
  not_needed: "rgba(106,138,154,0.35)",
};

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const LABEL_W = 220;
const WEEK_W = 36;

function toDate(val) {
  if (!val) return null;
  if (val?.toDate) return val.toDate();
  const d = new Date(val);
  return isNaN(d) ? null : d;
}
function startOfDay(d) {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}
function buildMonths(from) {
  const months = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(from.getFullYear(), from.getMonth() + i, 1);
    months.push({ year: d.getFullYear(), month: d.getMonth() });
  }
  return months;
}
function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
function totalDays(months) {
  return months.reduce((s, m) => s + daysInMonth(m.year, m.month), 0);
}
function dayOffset(ganttStart, date) {
  return Math.round((startOfDay(date) - ganttStart) / 86_400_000);
}

export default function TimelineView({ steps, supplierId }) {
  const headerScrollRef = useRef(null);
  const gridScrollRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const [hovered, setHovered] = useState(null);

  const ganttStart = useMemo(() => {
    const dates = Object.values(steps)
      .map((sd) => toDate(sd.startDate))
      .filter(Boolean);
    const earliest =
      dates.length > 0
        ? new Date(Math.min(...dates.map((d) => d.getTime())))
        : new Date();
    earliest.setDate(1);
    earliest.setHours(0, 0, 0, 0);
    return earliest;
  }, [steps]);

  const months = useMemo(() => buildMonths(ganttStart), [ganttStart]);
  const totalDays_ = useMemo(() => totalDays(months), [months]);
  const gridW = months.length * 4 * WEEK_W;
  const todayOffset = dayOffset(ganttStart, new Date());
  const todayX = (todayOffset / totalDays_) * gridW;

  useEffect(() => {
    const grid = gridScrollRef.current;
    const header = headerScrollRef.current;
    if (!grid || !header) return;
    const onGridScroll = () => {
      header.scrollLeft = grid.scrollLeft;
    };
    grid.addEventListener("scroll", onGridScroll, { passive: true });
    return () => grid.removeEventListener("scroll", onGridScroll);
  }, []);

  useEffect(() => {
    if (gridScrollRef.current) {
      gridScrollRef.current.scrollLeft = Math.max(0, todayX - 200);
    }
  }, [todayX]);
  // ── Click-and-drag pan ───────────────────────────────────────────────────────
  useEffect(() => {
    const el = gridScrollRef.current;
    if (!el) return;

    const onMouseDown = (e) => {
      isDragging.current = true;
      dragStartX.current = e.clientX;
      dragScrollLeft.current = el.scrollLeft;
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
    };

    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      el.scrollLeft = dragScrollLeft.current - (e.clientX - dragStartX.current);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      el.style.cursor = "default";
      el.style.userSelect = "";
    };

    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const rows = useMemo(() => {
    const out = [];
    PLM_CATEGORIES.forEach((cat) => {
      out.push({ type: "category", cat });
      cat.steps.forEach((title, i) => {
        const docId = stepDocId(supplierId, cat.id, i);
        const stepData = steps[docId] || {};
        out.push({ type: "step", docId, title, cat, stepIdx: i, stepData });
      });
    });
    return out;
  }, [steps, supplierId]);

  return (
    <div className={styles.ganttOuter}>
      {/* ── Legend ── */}
      <div className={styles.ganttLegend}>
        {[
          { key: "done", label: "Done", color: PROGRESS_COLORS.done },
          {
            key: "in_progress",
            label: "In Progress",
            color: PROGRESS_COLORS.in_progress,
          },
          {
            key: "waiting",
            label: "Waiting Approval",
            color: PROGRESS_COLORS.waiting,
          },
          {
            key: "not_started",
            label: "Not Started",
            color: PROGRESS_COLORS.not_started,
          },
        ].map((l) => (
          <div key={l.key} className={styles.ganttLegendItem}>
            <div
              className={styles.ganttLegendDot}
              style={{ background: l.color }}
            />
            <span className={styles.ganttLegendLabel}>{l.label}</span>
          </div>
        ))}
        <div className={styles.ganttTodayLegend}>
          <div className={styles.ganttTodayLegendLine} />
          <span className={styles.ganttLegendLabel}>Today</span>
        </div>
      </div>

      {/* ── Main table ── */}
      <div className={styles.ganttTableWrap}>
        {/* Sticky header */}
        <div className={styles.ganttStickyHeader}>
          <div className={styles.ganttLabelColHeader}>
            <span className={styles.ganttLabelColTitle}>CATEGORY / STEP</span>
          </div>
          <div className={styles.ganttHeaderScroll} ref={headerScrollRef}>
            <div style={{ width: gridW, position: "relative", flexShrink: 0 }}>
              <div className={styles.ganttMonthRow}>
                {months.map((m, mi) => (
                  <div key={mi} className={styles.ganttMonthCell}>
                    <span className={styles.ganttMonthLabel}>
                      {MONTH_NAMES[m.month]} {m.year}
                    </span>
                  </div>
                ))}
              </div>
              <div className={styles.ganttWeekRow}>
                {months.map((m, mi) =>
                  [1, 2, 3, 4].map((w) => (
                    <div key={`${mi}-${w}`} className={styles.ganttWeekCell}>
                      W{w}
                    </div>
                  )),
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className={styles.ganttBodyWrap}>
          {/* Label column */}
          <div className={styles.ganttLabelColBody}>
            {rows.map((row) => {
              if (row.type === "category") {
                return (
                  <div
                    key={`cat-${row.cat.id}`}
                    className={styles.ganttCatLabelRow}
                  >
                    <div
                      className={styles.ganttCatDot}
                      style={{ background: row.cat.color }}
                    />
                    <span
                      className={styles.ganttCatLabelText}
                      style={{ color: row.cat.color }}
                    >
                      {row.cat.name}
                    </span>
                  </div>
                );
              }
              return (
                <div
                  key={row.docId}
                  className={styles.ganttStepLabelRow}
                  style={{
                    background:
                      hovered === row.docId
                        ? "rgba(200,169,110,0.06)"
                        : "transparent",
                  }}
                  onMouseEnter={() => {
                    if (!isDragging.current) setHovered(row.docId);
                  }}
                  onMouseLeave={() => setHovered(null)}
                >
                  <span className={styles.ganttStepLabelText} title={row.title}>
                    {row.title}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Grid */}
          <div className={styles.ganttGridScroll} ref={gridScrollRef}>
            <div
              style={{ width: gridW, position: "relative", minHeight: "100%" }}
            >
              {/* Month boundary lines */}
              {months.map(
                (m, mi) =>
                  mi > 0 && (
                    <div
                      key={mi}
                      className={`${styles.ganttGridLine} ${styles.ganttMonthLine}`}
                      style={{ left: mi * 4 * WEEK_W }}
                    />
                  ),
              )}

              {/* Week lines */}
              {months.map((_, mi) =>
                [1, 2, 3].map((w) => (
                  <div
                    key={`wl-${mi}-${w}`}
                    className={`${styles.ganttGridLine} ${styles.ganttWeekLine}`}
                    style={{ left: (mi * 4 + w) * WEEK_W }}
                  />
                )),
              )}

              {/* Today line */}
              {todayOffset >= 0 && todayOffset <= totalDays_ && (
                <div className={styles.ganttTodayLine} style={{ left: todayX }}>
                  <div className={styles.ganttTodayLabel}>TODAY</div>
                </div>
              )}

              {/* Rows */}
              {rows.map((row, ri) => {
                if (row.type === "category") {
                  return (
                    <div
                      key={`cg-${row.cat.id}`}
                      className={styles.ganttCatGridRow}
                      style={{
                        borderTop:
                          ri > 0 ? "1px solid rgba(232,223,200,0.12)" : "none",
                      }}
                    >
                      <div
                        className={styles.ganttCatBand}
                        style={{ background: `${row.cat.color}08` }}
                      />
                    </div>
                  );
                }

                const sd = row.stepData;
                const startDate = toDate(sd.startDate);
                const endDate = toDate(sd.endDate);
                const status = sd.status || "not_started";
                const palette = BAR_PALETTES[row.stepIdx % BAR_PALETTES.length];

                const progress = (() => {
                  if (status === "done") return 100;
                  if (status === "not_started") return 0;
                  if (startDate && endDate) {
                    const total = endDate - startDate;
                    const elapsed = Math.min(Date.now() - startDate, total);
                    return Math.max(
                      0,
                      Math.min(95, Math.round((elapsed / total) * 100)),
                    );
                  }
                  return status === "in_progress" ? 50 : 0;
                })();

                const renderBar = (start, end) => {
                  if (!start || !end) return null;
                  const s = dayOffset(ganttStart, start);
                  const e = dayOffset(ganttStart, end);
                  if (e < 0 || s > totalDays_) return null;
                  const clampS = Math.max(0, s);
                  const clampE = Math.min(totalDays_, e);
                  const leftPx = (clampS / totalDays_) * gridW;
                  const widthPx = Math.max(
                    12,
                    ((clampE - clampS) / totalDays_) * gridW,
                  );

                  return (
                    <div
                      key="bar"
                      style={{
                        position: "absolute",
                        left: leftPx,
                        top: "calc(50% - 7px)",
                        width: widthPx,
                        height: 14,
                        borderRadius: 4,
                        background: palette[0],
                        border: `1px solid ${palette[1]}`,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        boxShadow: `0 2px 8px ${palette[1]}44`,
                        cursor: "default",
                      }}
                    >
                      {/* Progress fill — must be inline (runtime width + status color) */}
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: `${progress}%`,
                          background:
                            PROGRESS_COLORS[status] ||
                            PROGRESS_COLORS.not_started,
                          borderRadius: 3,
                          transition: "width 0.4s ease",
                        }}
                      />
                      {widthPx > 40 && (
                        <span
                          className={styles.ganttBarLabel}
                          style={{ color: palette[2], maxWidth: widthPx - 8 }}
                        >
                          {row.title}
                        </span>
                      )}
                    </div>
                  );
                };

                return (
                  <div
                    key={row.docId}
                    className={styles.ganttStepGridRow}
                    style={{
                      background:
                        hovered === row.docId
                          ? "rgba(200,169,110,0.05)"
                          : ri % 2 === 0
                            ? "transparent"
                            : "rgba(0,0,0,0.07)",
                    }}
                    onMouseEnter={() => {
                      if (!isDragging.current) setHovered(row.docId);
                    }}
                    onMouseLeave={() => setHovered(null)}
                  >
                    {renderBar(startDate, endDate)}
                    {hovered === row.docId && startDate && (
                      <Tooltip
                        title={row.title}
                        cat={row.cat}
                        status={status}
                        startDate={startDate}
                        endDate={endDate}
                        progress={progress}
                        leftX={Math.max(
                          0,
                          (dayOffset(ganttStart, startDate) / totalDays_) *
                            gridW,
                        )}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TOOLTIP ─────────────────────────────────────────────────────────────────
function fmt(d) {
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function Tooltip({ title, cat, status, startDate, endDate, progress, leftX }) {
  const duration =
    startDate && endDate
      ? Math.round((endDate - startDate) / 86_400_000) + "d"
      : "—";
  const statusLabel =
    {
      done: "Done",
      in_progress: "In Progress",
      waiting: "Waiting Approval",
      not_started: "Not Started",
      not_needed: "Not Needed",
    }[status] || status;

  return (
    <div
      className={styles.ganttTooltip}
      style={{
        left: Math.min(leftX, 900),
        border: `1px solid ${cat.color}55`,
      }}
    >
      <div className={styles.ganttTooltipCat} style={{ color: cat.color }}>
        {cat.name}
      </div>
      <div className={styles.ganttTooltipTitle}>{title}</div>
      <div className={styles.ganttTooltipGrid}>
        {[
          ["Start Date", fmt(startDate)],
          ["End Date", fmt(endDate)],
          ["Duration", duration],
          ["Progress", `${progress}%`],
          ["Status", statusLabel],
        ].map(([k, v]) => (
          <div key={k} className={styles.ganttTooltipField}>
            <span className={styles.ganttTooltipFieldKey}>{k}</span>
            <span className={styles.ganttTooltipFieldVal}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
